import { useState, useEffect, useRef } from "react";
import {
  Loader2,
  Clock,
  Terminal,
  ExternalLink,
  Mail,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { DevLoginPanel } from "@/components/dev-login-panel";
import { useDevUsers } from "@/lib/dev-auth";
import { GithubIcon, GoogleIcon } from "@/components/icons";
import { IamuWordmark } from "@/components/brand/logo";

const APP_URL =
  (import.meta.env.VITE_WEB_APP_URL as string | undefined) ||
  "https://iamu.dev";

function getSafeRedirect(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("redirect");
  if (raw && raw.startsWith("/") && !raw.startsWith("//")) {
    return raw;
  }
  return "/";
}

function getInviteToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const invite = params.get("invite");
  if (invite) return invite;
  const redirect = params.get("redirect");
  if (redirect) {
    const match = redirect.match(/^\/invite\/([a-zA-Z0-9_-]+)/);
    if (match) return match[1];
  }
  return null;
}

export function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [isEarlyAccessPending, setIsEarlyAccessPending] = useState(false);
  const [earlyAccessEmail, setEarlyAccessEmail] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(() => {
    return new URLSearchParams(window.location.search).get("mode") === "signup";
  });
  const [verifyId, setVerifyId] = useState<string | null>(() => {
    return new URLSearchParams(window.location.search).get("verify");
  });
  const [verifyEmail, setVerifyEmail] = useState<string | null>(() => {
    return new URLSearchParams(window.location.search).get("email");
  });
  const [verifyCode, setVerifyCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const devUsers = useDevUsers();

  const redirectTo = getSafeRedirect();

  useEffect(() => {
    if (verifyId) {
      inputRef.current?.focus();
    }
  }, [verifyId]);

  // Show error if redirected back from failed OIDC flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oidcError = params.get("error");
    const emailParam = params.get("email");
    if (oidcError) {
      if (oidcError === "early_access_pending") {
        setIsEarlyAccessPending(true);
        setEarlyAccessEmail(emailParam);
      } else if (oidcError === "email_verification_required") {
        setError(
          "Please check your email to verify your address before continuing.",
        );
      } else if (oidcError === "mfa_required") {
        setError(
          "Two-factor authentication or additional verification is required. Please try signing in again.",
        );
      } else if (oidcError === "exchange_failed") {
        setError("Sign-in failed. Please try again.");
      } else {
        setError(`Authentication error: ${oidcError}`);
      }
    }
  }, []);

  // Reset loading state when navigating back via browser history (bfcache)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setLoading(null);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Cooldown countdown for resending verification code
  useEffect(() => {
    if (!verifyId || resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [verifyId, resendCooldown]);

  async function submitVerification(codeToVerify: string) {
    if (!verifyId || codeToVerify.length !== 6 || isVerifying) return;
    setIsVerifying(true);
    setError(null);
    setResendSuccess(false);

    try {
      const res = await fetch("/api/auth/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verifyId, code: codeToVerify }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data.error ||
            "Invalid or expired verification code. Please check your inbox and try again.",
        );
        setIsVerifying(false);
        return;
      }
      window.location.href = data.redirectUrl || redirectTo;
    } catch {
      setError("Network error while verifying code. Please try again.");
      setIsVerifying(false);
    }
  }

  async function handleResendCode() {
    if (!verifyId || resendCooldown > 0 || isResending) return;
    setIsResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verifyId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data.error ||
            "Unable to resend verification code. Please try again later.",
        );
      } else {
        setResendSuccess(true);
        setResendCooldown(30);
      }
    } catch {
      setError("Network error while resending code. Please try again.");
    } finally {
      setIsResending(false);
    }
  }

  function handleCancelVerification() {
    setVerifyId(null);
    setVerifyEmail(null);
    setVerifyCode("");
    setError(null);
    setResendSuccess(false);
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("verify");
    cleanUrl.searchParams.delete("email");
    window.history.replaceState({}, "", cleanUrl.toString());
  }

  function handleSignIn(provider: "GoogleOAuth" | "GitHubOAuth") {
    setError(null);
    setLoading(provider);
    const params = new URLSearchParams({ redirect: redirectTo });
    const invite = getInviteToken();
    if (invite) params.set("invite", invite);
    params.set("provider", provider);
    if (isSignUp) params.set("mode", "signup");
    window.location.href = `/api/auth/login?${params.toString()}`;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 pb-10 pt-6 sm:px-10 lg:px-14">
        <header className="flex items-center justify-between">
          <a
            href={APP_URL}
            className="transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
            aria-label="Iamu home"
          >
            <IamuWordmark />
          </a>
          <a
            href={APP_URL}
            className="rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition-colors hover:bg-foreground/90"
          >
            Public site
          </a>
        </header>

        <main className="flex flex-1 items-center py-10">
          <div className="grid w-full gap-14 lg:grid-cols-[minmax(0,1fr)_430px] lg:items-center lg:gap-20">
            <section className="max-w-xl">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {isSignUp ? "Get started with Iamu." : "Welcome to Iamu."}
              </h1>

              <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">
                Continuous persona walkthroughs, acceptance gates, and findings
                triage for your team.
              </p>
            </section>

            <section className="w-full max-w-[430px] lg:justify-self-end">
              <div className="rounded-[32px] border border-border bg-card/90 px-6 py-7 shadow-[0_18px_60px_rgba(22,19,17,0.08)] backdrop-blur-xl sm:px-8 sm:py-9">
                {isEarlyAccessPending ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
                        <Clock className="h-3.5 w-3.5" />
                        Early Access
                      </span>
                    </div>

                    <div className="space-y-2">
                      <h2 className="text-xl font-semibold tracking-tight text-foreground">
                        You&apos;re on the list
                      </h2>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Iamu Cloud is currently in private early access.{" "}
                        {earlyAccessEmail ? (
                          <>
                            We&apos;ve added{" "}
                            <strong className="font-semibold text-foreground">
                              {earlyAccessEmail}
                            </strong>{" "}
                            to our queue. We&apos;ll notify you as soon as an
                            invitation opens up.
                          </>
                        ) : (
                          "We've added your account to our queue. We'll notify you as soon as an invitation opens up."
                        )}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground space-y-2">
                      <div className="flex items-center gap-1.5 font-medium text-foreground">
                        <Terminal className="h-3.5 w-3.5" />
                        Iamu CLI is free and offline
                      </div>
                      <p className="leading-relaxed">
                        The CLI and local compliance checking tools are
                        open-source. You can run automated persona walkthroughs
                        and evaluate gates locally in your terminal or CI today.
                      </p>
                      <a
                        href={`${APP_URL}/docs`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-4 hover:opacity-80"
                      >
                        View documentation
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsEarlyAccessPending(false);
                          setEarlyAccessEmail(null);
                          const cleanUrl = new URL(window.location.href);
                          cleanUrl.searchParams.delete("error");
                          cleanUrl.searchParams.delete("email");
                          window.history.replaceState(
                            {},
                            "",
                            cleanUrl.toString(),
                          );
                        }}
                        className="flex h-11 w-full items-center justify-center rounded-full border border-border bg-background px-4 text-sm font-semibold text-foreground transition-all hover:bg-muted active:scale-[0.99]"
                      >
                        Sign in with a different account
                      </button>
                      <a
                        href={APP_URL}
                        className="flex h-10 w-full items-center justify-center rounded-full text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        Back to public site
                      </a>
                    </div>
                  </div>
                ) : verifyId ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary-ink">
                        <Mail className="h-3.5 w-3.5" />
                        Verification
                      </span>
                    </div>

                    <div className="space-y-2">
                      <h2 className="text-xl font-semibold tracking-tight text-foreground">
                        Check your inbox
                      </h2>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        We sent a 6-digit verification code to{" "}
                        {verifyEmail ? (
                          <strong className="font-semibold text-foreground">
                            {verifyEmail}
                          </strong>
                        ) : (
                          "your email address"
                        )}
                        . Enter it below to complete sign-in.
                      </p>
                    </div>

                    {error && (
                      <div
                        role="alert"
                        id="verify-error"
                        className="rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive"
                      >
                        {error}
                      </div>
                    )}

                    {resendSuccess && (
                      <div
                        role="status"
                        className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs text-emerald-600 dark:text-emerald-400"
                      >
                        A fresh verification code has been sent to your inbox.
                      </div>
                    )}

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitVerification(verifyCode);
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={verifyCode}
                          aria-label="6-digit verification code"
                          aria-invalid={Boolean(error)}
                          aria-describedby={error ? "verify-error" : undefined}
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6);
                            setVerifyCode(val);
                            setError(null);
                            if (val.length === 6) {
                              submitVerification(val);
                            }
                          }}
                          placeholder="••••••"
                          ref={inputRef}
                          className="h-14 w-full rounded-2xl border border-border bg-background px-4 text-center font-mono text-2xl text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all placeholder:font-sans placeholder:text-muted-foreground"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={verifyCode.length < 6 || isVerifying}
                        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:opacity-50"
                      >
                        {isVerifying ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Verify & continue"
                        )}
                      </button>
                    </form>

                    <div className="flex flex-col items-center gap-3 pt-2 text-xs">
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={resendCooldown > 0 || isResending}
                        className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                      >
                        {resendCooldown > 0
                          ? `Didn't get a code? Resend in ${resendCooldown}s`
                          : isResending
                            ? "Sending..."
                            : "Didn't receive a code? Resend"}
                      </button>

                      <button
                        type="button"
                        onClick={handleCancelVerification}
                        className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
                      >
                        Cancel and return to sign in
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-7">
                      {devUsers && (
                        <div className="text-sm font-medium text-primary">
                          Local dev environment
                        </div>
                      )}
                      <h2 className="text-lg font-semibold tracking-tight text-foreground">
                        {devUsers
                          ? "Sign in as"
                          : isSignUp
                            ? "Create your account"
                            : "Sign in"}
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {devUsers
                          ? "Seeded fixture users. Reset with pnpm db:local:reset."
                          : isSignUp
                            ? "Continue with your provider to set up your workspace."
                            : "Sign in with your account to continue."}
                      </p>
                    </div>

                    {devUsers ? (
                      <DevLoginPanel users={devUsers} redirectTo={redirectTo} />
                    ) : (
                      <>
                        {error && (
                          <p className="mb-3 text-sm text-destructive">
                            {error}
                          </p>
                        )}
                        <div className="flex flex-col gap-3">
                          <button
                            type="button"
                            onClick={() => handleSignIn("GoogleOAuth")}
                            disabled={Boolean(loading)}
                            className="flex h-12 w-full items-center justify-center gap-3 rounded-full border border-border bg-background px-5 text-sm font-semibold text-foreground transition-all hover:bg-muted active:scale-[0.99] disabled:opacity-50"
                          >
                            {loading === "GoogleOAuth" ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <GoogleIcon className="h-5 w-5" />
                            )}
                            Continue with Google
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSignIn("GitHubOAuth")}
                            disabled={Boolean(loading)}
                            className="flex h-12 w-full items-center justify-center gap-3 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:opacity-50"
                          >
                            {loading === "GitHubOAuth" ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <GithubIcon className="h-5 w-5 fill-current" />
                            )}
                            Continue with GitHub
                          </button>
                        </div>

                        <div className="mt-5 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              const next = !isSignUp;
                              setIsSignUp(next);
                              const cleanUrl = new URL(window.location.href);
                              if (next) {
                                cleanUrl.searchParams.set("mode", "signup");
                              } else {
                                cleanUrl.searchParams.delete("mode");
                              }
                              window.history.replaceState(
                                {},
                                "",
                                cleanUrl.toString(),
                              );
                            }}
                            className="text-xs text-muted-foreground transition-colors hover:text-foreground underline underline-offset-4"
                          >
                            {isSignUp
                              ? "Already have an account? Sign in"
                              : "New to Iamu? Create an account"}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <p className="mt-6 px-1 text-center text-xs text-muted-foreground">
                By continuing, you agree to Iamu&apos;s{" "}
                <a
                  href={`${APP_URL}/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href={`${APP_URL}/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
